import { styled } from "goober";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { dragIndicatorHidden, dragIndicatorShown } from "./DragIndicator";
import { MOTION_BASE_MS } from "./motion";

// How close to both a handle and a perpendicular nested handle a press must be
// to grab both, so the meeting point of two splits drags in 2D.
const CORNER_REACH = 24;
const CORNER_CURSOR = "nwse-resize";

type CrossHandle = {
  handleRef: React.RefObject<HTMLDivElement | null>;
  startDrag: (cursor: string, press: Point) => void;
  reset: () => void;
};

type Point = { clientX: number; clientY: number };

const CrossHandlesContext = createContext<{
  orientation: Props["orientation"];
  handles: Set<CrossHandle>;
  hoverCorner: (point: Point | null) => boolean;
  pressCorner: (e: React.MouseEvent) => boolean;
} | null>(null);

function within(value: number, start: number, end: number, reach = 0) {
  return value >= start - reach && value <= end + reach;
}

function loadPanelFraction(storageKey: string) {
  const stored = localStorage.getItem(storageKey);
  if (stored == null) return undefined;
  const fraction = Number(stored);
  return Number.isFinite(fraction) ? fraction : undefined;
}

export function usePanelFraction(storageKey: string, isDragging: boolean) {
  const [fraction, setFraction] = useState(() => loadPanelFraction(storageKey));
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey);
  if (prevStorageKey !== storageKey) {
    setPrevStorageKey(storageKey);
    setFraction(loadPanelFraction(storageKey));
  }
  useEffect(() => {
    if (isDragging) return;
    if (fraction === undefined) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, String(fraction));
  }, [fraction, isDragging, storageKey]);
  return [fraction, setFraction] as const;
}

const HandleContainer = styled("div")`
  z-index: 1;
  user-select: none;

  & [data-drag-indicator] {
    ${dragIndicatorHidden("translateY(-4px)")}
  }

  & [data-drag-indicator][data-orientation="horizontal"] {
    ${dragIndicatorHidden("translateX(4px)")}
  }

  &:hover:not([data-corner-hovered]) [data-drag-indicator] {
    ${dragIndicatorShown}
  }
`;

type Props = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  orientation: "vertical" | "horizontal";
  minSize?: number;
  maxSize?: number;
  minOppositeSize?: number;
  defaultSize?: React.CSSProperties["height"];
  // Dragged sizes persist under this key as a fraction of the container, so the
  // split keeps its proportions as the window resizes.
  storageKey: string;
  overrideSize?: React.CSSProperties["height"];
  // When true, the pane settles instantly (no transition) while it is neither
  // dragged nor overridden, so a controlled pane snaps into place as its content
  // loads instead of animating in. Defaults to the animated behaviour.
  snapUndraggedResize?: boolean;
  // Fires on the first move of a drag, not on press, so a click can't trigger it.
  onDragStart?: () => void;
  Handle?: React.ReactNode;
  first: React.ReactNode;
  second: React.ReactNode;
};

export function Resizable({
  containerRef,
  orientation,
  minSize = 0,
  maxSize,
  minOppositeSize = minSize,
  Handle = "div",
  defaultSize = "50%",
  storageKey,
  overrideSize,
  snapUndraggedResize = false,
  onDragStart,
  first,
  second,
}: Props) {
  // The pane follows the pointer's travel from the press rather than the
  // pointer itself, so grabbing a handle never nudges the pane.
  const dragOriginRef = useRef<{
    pointer: number;
    size: number;
    moved: boolean;
  } | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [dragCursor, setDragCursor] = useState<string | null>(null);
  const [draggedFraction, setDraggedFraction] = usePanelFraction(
    storageKey,
    dragCursor !== null
  );
  const [crossHandles] = useState(() => new Set<CrossHandle>());
  const [isCornerHovered, setIsCornerHovered] = useState(false);
  const [isParentCornerHovered, setIsParentCornerHovered] = useState(false);
  const parentCrossHandles = useContext(CrossHandlesContext);
  const resizeCursor = orientation === "vertical" ? "ns-resize" : "ew-resize";

  // Leaving an override would otherwise snap when a dragged size exists, so
  // the transition is kept through the exit and its settle window. Detect the
  // exit during render and hold the flag for the settle window in an effect.
  const [prevOverride, setPrevOverride] = useState(overrideSize);
  const [settlingFromOverride, setSettlingFromOverride] = useState(false);
  // Bumped on each override exit so a second exit within the settle window
  // restarts the timer rather than inheriting the first exit's expiry.
  const [exitToken, setExitToken] = useState(0);
  if (prevOverride !== overrideSize) {
    const wasOverridden = prevOverride !== undefined;
    setPrevOverride(overrideSize);
    if (wasOverridden && overrideSize === undefined) {
      setSettlingFromOverride(true);
      setExitToken((n) => n + 1);
    }
  }
  useEffect(() => {
    if (exitToken === 0) return;
    const t = setTimeout(() => setSettlingFromOverride(false), MOTION_BASE_MS);
    return () => clearTimeout(t);
  }, [exitToken]);

  const resetSize = useCallback(() => {
    dragOriginRef.current = null;
    setDragCursor(null);
    setDraggedFraction(undefined);
  }, [setDraggedFraction]);

  const startDrag = useCallback(
    (cursor: string, press: Point) => {
      const pane = paneRef.current?.getBoundingClientRect();
      dragOriginRef.current = {
        pointer: orientation === "vertical" ? press.clientY : press.clientX,
        size: (orientation === "vertical" ? pane?.height : pane?.width) ?? 0,
        moved: false,
      };
      setDragCursor(cursor);
    },
    [orientation]
  );

  useEffect(() => {
    if (
      !parentCrossHandles ||
      parentCrossHandles.orientation === orientation ||
      overrideSize !== undefined
    )
      return;
    const handle = { handleRef, startDrag, reset: resetSize };
    parentCrossHandles.handles.add(handle);
    return () => {
      parentCrossHandles.handles.delete(handle);
    };
  }, [parentCrossHandles, orientation, overrideSize, startDrag, resetSize]);

  const cornerAt = ({ clientX, clientY }: Point) => {
    if (overrideSize !== undefined || crossHandles.size === 0) return;
    const ownRect = handleRef.current?.getBoundingClientRect();
    if (!ownRect) return;
    const [across, along, acrossStart, acrossEnd, alongStart, alongEnd] =
      orientation === "vertical"
        ? ([clientY, clientX, "top", "bottom", "left", "right"] as const)
        : ([clientX, clientY, "left", "right", "top", "bottom"] as const);
    if (
      !within(across, ownRect[acrossStart], ownRect[acrossEnd], CORNER_REACH) ||
      !within(along, ownRect[alongStart], ownRect[alongEnd])
    )
      return;
    for (const handle of crossHandles) {
      const rect = handle.handleRef.current?.getBoundingClientRect();
      if (
        rect &&
        within(along, rect[alongStart], rect[alongEnd]) &&
        within(across, rect[acrossStart], rect[acrossEnd], CORNER_REACH)
      )
        return { handle, rect };
    }
  };

  const pressCorner = (e: React.MouseEvent) => {
    const match = cornerAt(e);
    if (!match) return false;
    if (e.detail > 1) {
      e.preventDefault();
      resetSize();
      match.handle.reset();
    } else {
      startDrag(CORNER_CURSOR, e);
      match.handle.startDrag(CORNER_CURSOR, e);
    }
    return true;
  };

  const hoverCorner = (point: Point | null) => {
    const isHovered = point !== null && cornerAt(point) !== undefined;
    setIsCornerHovered(isHovered);
    return isHovered;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (pressCorner(e) || parentCrossHandles?.pressCorner(e)) return;
    if (overrideSize !== undefined) return;
    if (e.detail > 1) {
      e.preventDefault();
      resetSize();
      return;
    }
    startDrag(resizeCursor, e);
  };

  const handleHover = (e: React.MouseEvent) => {
    const isOwnCorner = hoverCorner(e);
    setIsParentCornerHovered(
      parentCrossHandles?.hoverCorner(isOwnCorner ? null : e) ?? false
    );
  };

  const handleHoverEnd = () => {
    hoverCorner(null);
    parentCrossHandles?.hoverCorner(null);
    setIsParentCornerHovered(false);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const origin = dragOriginRef.current;
      if (!origin) return;
      const container = containerRef.current;
      if (!container) return;
      if (!origin.moved) {
        origin.moved = true;
        onDragStart?.();
      }
      const rect = container.getBoundingClientRect();
      const handleSize =
        handleRef.current?.getBoundingClientRect()[
          orientation === "vertical" ? "height" : "width"
        ] ?? 0;
      const computedStyle = getComputedStyle(container);
      const [leadingPadding, trailingPadding] =
        orientation === "vertical"
          ? [computedStyle.paddingTop, computedStyle.paddingBottom]
          : [computedStyle.paddingLeft, computedStyle.paddingRight];
      const newSize =
        origin.size +
        origin.pointer -
        (orientation === "vertical" ? e.clientY : e.clientX);
      const containerInnerSize =
        (orientation === "vertical" ? rect.height : rect.width) -
        parseFloat(leadingPadding) -
        parseFloat(trailingPadding);
      const upper = maxSize
        ? Math.min(maxSize, containerInnerSize - minOppositeSize - handleSize)
        : containerInnerSize - minOppositeSize - handleSize;
      const clamped = Math.max(minSize, Math.min(newSize, upper));
      setDraggedFraction(clamped / containerInnerSize);
    },
    [
      containerRef,
      maxSize,
      minSize,
      minOppositeSize,
      onDragStart,
      orientation,
      setDraggedFraction,
    ]
  );

  const size =
    overrideSize ??
    (draggedFraction === undefined
      ? typeof defaultSize === "number" && maxSize !== undefined
        ? Math.min(defaultSize, maxSize)
        : defaultSize
      : `max(${minSize}px, min(${draggedFraction * 100}%, 100% - ${minOppositeSize}px${maxSize === undefined ? "" : `, ${maxSize}px`}))`);

  const handleMouseUp = useCallback(() => {
    dragOriginRef.current = null;
    setDragCursor(null);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const sizeStyle =
    orientation === "vertical" ? { height: size } : { width: size };
  const isAtCorner = isCornerHovered || isParentCornerHovered;

  return (
    <CrossHandlesContext
      value={{ orientation, handles: crossHandles, hoverCorner, pressCorner }}
    >
      {first}

      <HandleContainer
        ref={handleRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleHover}
        onMouseLeave={handleHoverEnd}
        onDoubleClick={resetSize}
        data-corner-hovered={isAtCorner ? "" : undefined}
        style={{ cursor: isAtCorner ? CORNER_CURSOR : resizeCursor }}
      >
        {Handle}
      </HandleContainer>

      <div
        ref={paneRef}
        style={{
          ...sizeStyle,
          overflow: "hidden",
          transition:
            overrideSize !== undefined ||
            settlingFromOverride ||
            (!snapUndraggedResize && draggedFraction === undefined)
              ? `${orientation === "vertical" ? "height" : "width"} ${MOTION_BASE_MS}ms var(--ease-out)`
              : undefined,
        }}
      >
        {second}
      </div>

      {dragCursor !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            cursor: dragCursor,
          }}
        />
      )}
    </CrossHandlesContext>
  );
}
